import { useMemo, useState, useEffect, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/video/Breadcrumb";
import VideoCard from "@/components/video/VideoCard";
import { videos } from "@/mocks/videos";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);

  useDocumentTitle(
    query ? `“${query}”的搜索结果 - 黄瓜视频App官方网站` : "视频搜索 - 黄瓜视频App官方网站",
    "在黄瓜视频App官方网站搜索影视剧与短视频内容，快速找到感兴趣的视频资源。",
    "黄瓜视频App搜索,视频搜索,影视资源,短视频",
  );

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return videos.filter(
      (v) => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q),
    );
  }, [query]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <div className="w-full pt-20 md:pt-24">
      <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 border-b border-background-200/60">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />
        <h1 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-foreground-950">
          搜索结果
        </h1>
        <form onSubmit={submit} className="mt-6 max-w-xl">
          <div className="relative w-full">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-500 w-5 h-5 flex items-center justify-center" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入视频标题或简介关键词"
              className="w-full h-12 pl-11 pr-28 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>
        {query && (
          <p className="mt-4 text-sm text-foreground-500">
            关键词 “<span className="text-primary-400">{query}</span>” 共找到 {results.length} 条相关内容
          </p>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        {!query ? (
          <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200/60">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-search-line text-2xl" />
            </span>
            <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">输入关键词开始搜索</h3>
            <p className="mt-2 text-sm text-foreground-500">支持按视频标题或简介进行模糊搜索。</p>
          </div>
        ) : results.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {results.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center bg-background-100 rounded-lg border border-background-200/60">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-2xl" />
            </span>
            <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">没有找到相关内容</h3>
            <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或浏览推荐分类。</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category/电影"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-950 text-sm font-medium transition-colors cursor-pointer"
              >
                浏览电影分类
              </Link>
              <Link
                to="/category/短视频"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300/70 hover:border-primary-500 text-foreground-800 text-sm font-medium transition-colors cursor-pointer"
              >
                浏览短视频
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}